import React, {
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    IconButton,
    Paper,
    Portal,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';

import TickerExposureTable from '../TickerExposureTable';
import { uppercaseLabel } from '../../styles/typography';
import {
    MSI_GEOMETRY,
    APP_STYLE_PALETTE as MSI_PALETTE,
    MSI_TYPE_SCALE,
    usesAppStyleGrammar,
    resolveMsiSxRadius,
} from '../../styles/msiStyleTokens';

const VIEWPORT_PADDING = 16;
const KEYBOARD_MOVE_STEP = 16;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getViewportBounds = (panel) => ({
    maxX: Math.max(VIEWPORT_PADDING, window.innerWidth - panel.offsetWidth - VIEWPORT_PADDING),
    maxY: Math.max(VIEWPORT_PADDING, window.innerHeight - panel.offsetHeight - VIEWPORT_PADDING),
});

export default function FloatingFactorPanel({
    open,
    factorName,
    tickers,
    isLoading,
    error,
    containerRef,
    snapshotKey,
    onClose,
}) {
    const theme = useTheme();
    const panelRef = useRef(null);
    const dragStateRef = useRef(null);
    const previousFocusRef = useRef(null);
    const openedSnapshotKeyRef = useRef(null);
    const hasOpenedSnapshotRef = useRef(false);
    const titleId = useId();
    const [position, setPosition] = useState({ x: VIEWPORT_PADDING, y: VIEWPORT_PADDING });
    const [isDragging, setIsDragging] = useState(false);

    const clampPosition = useCallback((candidate) => {
        const panel = panelRef.current;
        if (!panel) return candidate;
        const { maxX, maxY } = getViewportBounds(panel);
        return {
            x: clamp(candidate.x, VIEWPORT_PADDING, maxX),
            y: clamp(candidate.y, VIEWPORT_PADDING, maxY),
        };
    }, []);

    const setPanelNode = useCallback((panel) => {
        panelRef.current = panel;
        if (!panel) return;

        previousFocusRef.current = document.activeElement;
        const container = containerRef.current;
        if (container) {
            const anchorRect = container.getBoundingClientRect();
            const { maxX, maxY } = getViewportBounds(panel);
            setPosition({
                x: clamp(
                    anchorRect.right - panel.offsetWidth - VIEWPORT_PADDING,
                    VIEWPORT_PADDING,
                    maxX,
                ),
                y: clamp(anchorRect.top + VIEWPORT_PADDING, VIEWPORT_PADDING, maxY),
            });
        }
        panel.focus();
    }, [containerRef]);

    useEffect(() => {
        if (!open) return undefined;

        const handleResize = () => {
            setPosition((current) => clampPosition(current));
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            const previousFocus = previousFocusRef.current;
            if (previousFocus instanceof HTMLElement && document.contains(previousFocus)) {
                previousFocus.focus();
            }
            previousFocusRef.current = null;
        };
    }, [clampPosition, open]);

    useEffect(() => {
        if (!open) {
            hasOpenedSnapshotRef.current = false;
            openedSnapshotKeyRef.current = null;
            return;
        }
        if (!hasOpenedSnapshotRef.current) {
            hasOpenedSnapshotRef.current = true;
            openedSnapshotKeyRef.current = snapshotKey;
            return;
        }
        if (openedSnapshotKeyRef.current !== snapshotKey) {
            onClose();
        }
    }, [onClose, open, snapshotKey]);

    useEffect(() => {
        if (!open) return undefined;
        const handleEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose, open]);

    useEffect(() => {
        if (!open || !isDragging) return undefined;

        const handlePointerMove = (event) => {
            const dragState = dragStateRef.current;
            if (!dragState) return;
            setPosition(clampPosition({
                x: event.clientX - dragState.offsetX,
                y: event.clientY - dragState.offsetY,
            }));
        };
        const handlePointerUp = () => {
            dragStateRef.current = null;
            setIsDragging(false);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [clampPosition, isDragging, open]);

    const handleDragStart = useCallback((event) => {
        if (event.button !== undefined && event.button !== 0) return;
        const panel = panelRef.current;
        if (!panel) return;
        const panelRect = panel.getBoundingClientRect();
        dragStateRef.current = {
            offsetX: event.clientX - panelRect.left,
            offsetY: event.clientY - panelRect.top,
        };
        setIsDragging(true);
        event.preventDefault();
    }, []);

    const handleMoveKey = useCallback((event) => {
        const delta = {
            ArrowLeft: { x: -KEYBOARD_MOVE_STEP, y: 0 },
            ArrowRight: { x: KEYBOARD_MOVE_STEP, y: 0 },
            ArrowUp: { x: 0, y: -KEYBOARD_MOVE_STEP },
            ArrowDown: { x: 0, y: KEYBOARD_MOVE_STEP },
        }[event.key];
        if (!delta) return;
        event.preventDefault();
        setPosition((current) => clampPosition({
            x: current.x + delta.x,
            y: current.y + delta.y,
        }));
    }, [clampPosition]);

    if (!open || !factorName) return null;

    return (
        <Portal>
            <div
                ref={setPanelNode}
                role="dialog"
                aria-modal="false"
                aria-labelledby={titleId}
                tabIndex={-1}
                style={{
                    position: 'fixed',
                    top: position.y,
                    left: position.x,
                    zIndex: theme.zIndex.modal + 2,
                    outline: 'none',
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        pointerEvents: 'auto',
                        width: { xs: 'calc(100vw - 32px)', sm: 'fit-content' },
                        minWidth: { sm: 430 },
                        maxWidth: 'min(90vw, 920px)',
                        maxHeight: 'calc(100vh - 32px)',
                        overflow: 'hidden',
                        borderRadius: (theme) => resolveMsiSxRadius(theme, MSI_GEOMETRY.radii.panel, 2.5),
                        border: (theme) => `1px solid ${
                            usesAppStyleGrammar(theme) ? MSI_PALETTE.line : alpha(theme.palette.divider, 0.9)
                        }`,
                        boxShadow: (theme) => {
                            if (usesAppStyleGrammar(theme)) {
                                return '0 8px 24px rgba(12, 23, 38, 0.12)';
                            }
                            return theme.palette.mode === 'dark'
                                ? '0 20px 44px rgba(0, 0, 0, 0.38)'
                                : '0 24px 48px rgba(15, 23, 42, 0.16)';
                        },
                        backgroundColor: (theme) => (
                            usesAppStyleGrammar(theme) ? MSI_PALETTE.surface : theme.palette.background.paper
                        ),
                        backgroundImage: (theme) => (usesAppStyleGrammar(theme) ? 'none' : undefined),
                    }}
                >
                    <Box
                        onPointerDown={handleDragStart}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            px: 2,
                            py: 1.25,
                            borderBottom: (theme) => `1px solid ${
                                usesAppStyleGrammar(theme) ? MSI_PALETTE.line : theme.palette.divider
                            }`,
                            backgroundColor: (theme) => (
                                usesAppStyleGrammar(theme)
                                    ? MSI_PALETTE.surfaceSubtle
                                    : alpha(theme.palette.action.hover, 0.18)
                            ),
                            cursor: isDragging ? 'grabbing' : 'grab',
                            userSelect: 'none',
                            touchAction: 'none',
                        }}
                    >
                        <Box
                            onKeyDown={handleMoveKey}
                            role="button"
                            tabIndex={0}
                            aria-label="Move factor details"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                '&:focus-visible': {
                                    outline: '2px solid',
                                    outlineColor: 'primary.main',
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            <DragIndicatorRoundedIcon fontSize="small" color="action" />
                            <Box>
                                <Typography
                                    id={titleId}
                                    variant="subtitle2"
                                    sx={uppercaseLabel({
                                        color: 'text.secondary',
                                        fontSize: (theme) => (
                                            usesAppStyleGrammar(theme)
                                                ? MSI_TYPE_SCALE.tableHeader.fontSize
                                                : '0.74rem'
                                        ),
                                        letterSpacing: (theme) => (usesAppStyleGrammar(theme) ? 0 : '0.08em'),
                                    })}
                                >
                                    Factor Details
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                    {factorName}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            size="small"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={onClose}
                            aria-label="Close factor details"
                            sx={{ color: 'text.secondary' }}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ p: 1.5, pt: 1, overflow: 'auto', maxHeight: 'calc(100vh - 106px)' }}>
                        <TickerExposureTable
                            factorName={factorName}
                            tickers={tickers}
                            isLoading={isLoading}
                            error={error}
                            hideTitle
                            withPaper={false}
                            maxHeight={430}
                        />
                    </Box>
                </Paper>
            </div>
        </Portal>
    );
}

FloatingFactorPanel.propTypes = {
    open: PropTypes.bool.isRequired,
    factorName: PropTypes.string,
    tickers: PropTypes.arrayOf(PropTypes.object),
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    containerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
    snapshotKey: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};
